'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface PNRStatus {
  pnr: string;
  status: 'confirmed' | 'pending' | 'cancelled' | 'waitlisted';
  airline: string;
  airlineLogo: string;
  route: string;
  departure: string;
  arrival: string;
  passenger: string;
  bookingRef: string;
  seatNumber: string;
  lastUpdated: string;
}

interface PNRStatusCardProps {
  pnr: PNRStatus;
}

const PNRStatusCard = ({ pnr }: PNRStatusCardProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-success text-success-foreground';
      case 'pending':
        return 'bg-warning text-warning-foreground';
      case 'cancelled':
        return 'bg-error text-error-foreground';
      case 'waitlisted':
        return 'bg-secondary text-secondary-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'CheckCircleIcon';
      case 'pending':
        return 'ClockIcon';
      case 'cancelled':
        return 'XCircleIcon';
      case 'waitlisted':
        return 'QueueListIcon';
      default:
        return 'QuestionMarkCircleIcon';
    }
  };

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-lg shadow-brand p-6 border border-border">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/3"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
          <div className="h-4 bg-muted rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-brand hover:shadow-brand-lg transition-all duration-300 p-6 border border-border">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-surface rounded-lg flex items-center justify-center overflow-hidden">
            <img 
              src={pnr.airlineLogo} 
              alt={`${pnr.airline} logo`}
              className="w-full h-full object-contain"
            />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-lg text-foreground">
              {pnr.airline}
            </h3>
            <p className="text-sm text-muted-foreground">PNR: {pnr.pnr}</p>
          </div>
        </div>
        <div className={`px-3 py-1.5 rounded-full flex items-center space-x-2 ${getStatusColor(pnr.status)}`}>
          <Icon name={getStatusIcon(pnr.status) as any} size={16} />
          <span className="text-xs font-semibold uppercase">{pnr.status}</span>
        </div>
      </div>

      <div className="space-y-3 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="MapPinIcon" size={18} className="text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">{pnr.route}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Departure</p>
            <p className="text-sm font-medium text-foreground">{pnr.departure}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Arrival</p>
            <p className="text-sm font-medium text-foreground">{pnr.arrival}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Passenger</p>
            <p className="text-sm font-medium text-foreground">{pnr.passenger}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Seat</p>
            <p className="text-sm font-medium text-foreground">{pnr.seatNumber}</p>
          </div>
        </div>

        <div>
          <p className="text-xs text-muted-foreground mb-1">Booking Reference</p>
          <p className="text-sm font-mono font-medium text-foreground">{pnr.bookingRef}</p>
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div className="flex items-center space-x-2 text-xs text-muted-foreground">
          <Icon name="ClockIcon" size={14} />
          <span>Updated: {pnr.lastUpdated}</span>
        </div>
        <button className="text-sm font-medium text-primary hover:text-secondary transition-colors duration-200">
          View Details
        </button>
      </div>
    </div>
  );
};

export default PNRStatusCard;